import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Map,
  BarChart3,
  TrendingUp,
  Layers,
  Network,
  Globe,
  Building2,
  Landmark,
  CircuitBoard,
  DollarSign,
  ShieldCheck,
  CloudLightning,
  Users,
  ChevronRight,
  Menu,
  X,
  Linkedin,
  Instagram,
  MapPin,
  Target,
  Zap,
  Eye,
  Activity,
  Briefcase,
  HeartHandshake,
  Radio,
  Search,
  Sparkles,
  PlayCircle,
  ChevronLeft,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Adaptive Logo component - uses the uploaded Nexintell logo
const Logo = ({ className = "h-8" }) => (
  <img
    src="https://customer-assets.emergentagent.com/job_smartgrid-hub-3/artifacts/huzue581_Nexintell%20logo.png"
    alt="Nexintell"
    className={`${className} object-contain`}
  />
);

// Track CTA click
const trackCTA = async (ctaName, ctaType, pageSource) => {
  try {
    await axios.post(`${API}/cta/track`, {
      cta_name: ctaName,
      cta_type: ctaType,
      page_source: pageSource,
      user_agent: navigator.userAgent,
      referrer: document.referrer
    });
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('CTA tracking failed:', e);
    }
  }
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Navigation
const Navigation = ({ onDemoClick }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/" className="flex items-center" data-testid="logo-link">
            <Logo className="h-8 md:h-10 lg:h-12" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#problem" className="nav-link" data-testid="nav-problem">Problem</a>
            <a href="#approach" className="nav-link" data-testid="nav-approach">Approach</a>
            <a href="#infralytics" className="nav-link" data-testid="nav-infralytics">Infralytics</a>
            <a href="#adoptic" className="nav-link" data-testid="nav-adoptic">Adoptic</a>
            <a href="#audience" className="nav-link" data-testid="nav-audience">Who It's For</a>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <a href="/admin" className="nav-link" data-testid="nav-admin">Admin</a>
            <Button
              onClick={() => { trackCTA('Request Demo Nav', 'demo_request', 'navigation'); onDemoClick(); }}
              className="btn-primary"
              data-testid="nav-demo-btn"
            >
              Request Demo
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="md:hidden bg-[#0a0a0a] border-t border-white/5"
        >
          <div className="px-6 py-4 space-y-4">
            <a href="#problem" className="block nav-link py-2">Problem</a>
            <a href="#approach" className="block nav-link py-2">Approach</a>
            <a href="#infralytics" className="block nav-link py-2">Infralytics</a>
            <a href="#adoptic" className="block nav-link py-2">Adoptic</a>
            <a href="#audience" className="block nav-link py-2">Who It's For</a>
            <a href="/admin" className="block nav-link py-2">Admin</a>
            <Button
              onClick={() => { trackCTA('Request Demo Mobile', 'demo_request', 'navigation'); onDemoClick(); setMobileOpen(false); }}
              className="btn-primary w-full"
            >
              Request Demo
            </Button>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onDemoClick, onExploreClick }) => (
  <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
    {/* Background */}
    <div className="absolute inset-0 hero-glow" />
    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1697360855755-eaf8c2779c7c?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center opacity-5" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
      <motion.div
        initial="initial"
        animate="animate"
        variants={stagger}
        className="max-w-4xl"
      >
        <motion.p
          variants={fadeInUp}
          className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-6"
        >
          Infrastructure × Consequence Intelligence
        </motion.p>

        <motion.h1
          variants={fadeInUp}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-none mb-8"
          data-testid="hero-title"
        >
          From the cable in the ground to the business it serves.
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          className="text-lg md:text-xl leading-relaxed text-[#a1a1a1] mb-10 max-w-3xl"
          data-testid="hero-subtitle"
        >
          Nexintell is the UK's first two-dimensional infrastructure intelligence platform — measuring both the <span className="text-white">presence</span> of digital infrastructure and its real <span className="text-white">impact</span> on the people and businesses it serves. Built on two products: <span className="text-white font-medium">Infralytics-GIIS</span>, the infrastructure layer, and <span className="text-white font-medium">Adoptic</span>, the consequence layer.
        </motion.p>

        <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 mb-12">
          <Button
            onClick={() => { trackCTA('Request a Demo', 'demo_request', 'hero'); onDemoClick(); }}
            className="btn-primary text-lg px-10 py-6"
            data-testid="hero-demo-btn"
          >
            Request a Demo
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            onClick={() => { trackCTA('Explore Platform', 'explore_product', 'hero'); onExploreClick(); }}
            className="btn-secondary text-lg px-8 py-6"
            data-testid="hero-explore-btn"
          >
            Explore the Platform
          </Button>
        </motion.div>

        {/* Two-product strip */}
        <motion.div variants={fadeInUp} className="grid sm:grid-cols-2 gap-4 max-w-3xl">
          <a href="#infralytics" className="group glass-panel rounded-xl p-5 border border-white/10 hover:border-[#ff4b2a]/40 transition-colors" data-testid="hero-infralytics-card">
            <div className="flex items-center gap-3 mb-2">
              <Network className="h-5 w-5 text-[#ff4b2a]" />
              <p className="font-mono text-xs uppercase tracking-widest text-[#a1a1a1]">Layer 01 · Infrastructure</p>
            </div>
            <p className="text-white font-semibold">Infralytics-GIIS</p>
            <p className="text-sm text-[#a1a1a1] mt-1">Global Infrastructure Intelligence System</p>
          </a>
          <a href="#adoptic" className="group glass-panel rounded-xl p-5 border border-white/10 hover:border-[#ff4b2a]/40 transition-colors" data-testid="hero-adoptic-card">
            <div className="flex items-center gap-3 mb-2">
              <Eye className="h-5 w-5 text-[#ff4b2a]" />
              <p className="font-mono text-xs uppercase tracking-widest text-[#a1a1a1]">Layer 02 · Consequence</p>
            </div>
            <p className="text-white font-semibold">Adoptic</p>
            <p className="text-sm text-[#a1a1a1] mt-1">Business Digital Inclusion Intelligence</p>
          </a>
        </motion.div>

        <motion.p
          variants={fadeInUp}
          className="text-sm text-[#a1a1a1] mt-10"
        >
          Founded in the UK. Built for institutions responsible for critical digital systems.
        </motion.p>
      </motion.div>
    </div>
  </section>
);

// Problem Section
const ProblemSection = () => (
  <section id="problem" className="section bg-[#0a0a0a]" data-testid="problem-section">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">The Challenge</p>
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-8" data-testid="problem-title">
          The UK has world-class infrastructure data — and an invisible adoption gap.
        </h2>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div className="space-y-6 text-lg text-[#a1a1a1] leading-relaxed">
            <p>
              Digital infrastructure now governs economic growth, resilience, and inclusion. Yet the systems societies depend on remain <span className="text-white font-medium">fragmented, opaque, and difficult to govern</span>.
            </p>
            <p>
              <span className="text-white font-medium">95% of UK premises</span> have ultra-fast broadband coverage. Meanwhile, <span className="text-white font-medium">55% of businesses</span> remain invisible online — concentrated in deprived areas. The cable is there. The participation is not.
            </p>
            <p className="text-white font-semibold text-xl pt-4">
              This is not a data problem.<br />
              It is an <span className="text-[#ff4b2a]">infrastructure intelligence</span> problem.
            </p>
            <p className="text-base">
              Infrastructure without adoption is incomplete. You cannot govern, plan, or invest in a system you can only see one half of.
            </p>
          </div>

          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1709396744297-5248893d595b?q=80&w=1000&auto=format&fit=crop"
              alt="Digital infrastructure visualization"
              className="rounded-2xl border border-white/10 opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent rounded-2xl" />
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// Why Tools Fail Section — Competitive frame
const WhyToolsFailSection = () => {
  const failures = [
    {
      icon: Map,
      label: "Unlike Ofcom dashboards",
      text: "They tell you where the cable is — but not whether a single business on that street is using it, benefiting from it, or being left behind."
    },
    {
      icon: BarChart3,
      label: "Unlike ESRI / IDOX",
      text: "They manage infrastructure assets beautifully — but cannot model human impact or socioeconomic consequence."
    },
    {
      icon: TrendingUp,
      label: "Unlike consultancy reports",
      text: "Six months and £50,000 to produce a report that is outdated before it is printed — and cannot be rerun."
    }
  ];

  return (
    <section className="section bg-[#111111]" data-testid="why-tools-fail-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">The Gap</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-12">
            Why existing tools fall short.
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {failures.map((item, idx) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="feature-card group"
                data-testid={`failure-card-${idx}`}
              >
                <item.icon className="h-10 w-10 text-[#ff4b2a] mb-4" />
                <p className="font-mono text-xs uppercase tracking-widest text-[#a1a1a1] mb-3">{item.label}</p>
                <p className="text-base text-white leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-xl md:text-2xl text-[#a1a1a1] max-w-3xl">
            Understanding infrastructure requires <span className="text-white font-medium">intelligence</span> — not visualisation alone. And measuring impact requires a second dimension entirely.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Two-Dimensional Approach Section (replaces CategorySection)
const ApproachSection = () => (
  <section id="approach" className="section bg-[#0a0a0a] relative" data-testid="approach-section">
    <div className="absolute inset-0 hero-glow opacity-50" />
    <div className="max-w-7xl mx-auto relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center max-w-3xl mx-auto mb-16"
      >
        <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">Our Approach</p>
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-8" data-testid="approach-title">
          A two-dimensional approach to infrastructure intelligence.
        </h2>
        <p className="text-lg md:text-xl text-[#a1a1a1] leading-relaxed">
          Nexintell addresses the UK's infrastructure intelligence gap with two complementary layers — together, the first UK platform to measure both the <span className="text-white">presence</span> of broadband infrastructure and its real <span className="text-white">impact</span> on the people and businesses it serves.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-panel rounded-2xl p-8 border border-white/10"
          data-testid="approach-infralytics-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#ff4b2a]/10 border border-[#ff4b2a]/30 flex items-center justify-center">
              <Network className="h-5 w-5 text-[#ff4b2a]" />
            </div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#a1a1a1]">Layer 01 · Infrastructure</p>
          </div>
          <h3 className="text-2xl md:text-3xl font-semibold mb-3">Infralytics-GIIS</h3>
          <p className="text-[#a1a1a1] leading-relaxed mb-6">
            The Global Infrastructure Intelligence System. Transforms fragmented connectivity, compute, and network data into structured, explainable, system-level intelligence — using 15 original composite indices across three analytical layers.
          </p>
          <a href="#infralytics" className="inline-flex items-center text-[#ff4b2a] font-medium hover:gap-3 gap-2 transition-all" data-testid="approach-infralytics-link">
            See Infralytics-GIIS <ChevronRight className="h-4 w-4" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-panel rounded-2xl p-8 border border-white/10"
          data-testid="approach-adoptic-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#ff4b2a]/10 border border-[#ff4b2a]/30 flex items-center justify-center">
              <Eye className="h-5 w-5 text-[#ff4b2a]" />
            </div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#a1a1a1]">Layer 02 · Consequence</p>
          </div>
          <h3 className="text-2xl md:text-3xl font-semibold mb-3">Adoptic</h3>
          <p className="text-[#a1a1a1] leading-relaxed mb-6">
            The UK's first Business Digital Inclusion Intelligence platform. Measures business digital adoption alongside connectivity infrastructure and deprivation — at the postcode level, in real time, for every registered business in a geography.
          </p>
          <a href="#adoptic" className="inline-flex items-center text-[#ff4b2a] font-medium hover:gap-3 gap-2 transition-all" data-testid="approach-adoptic-link">
            See Adoptic <ChevronRight className="h-4 w-4" />
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-center mt-16 pt-12 border-t border-white/10 max-w-3xl mx-auto"
      >
        <p className="text-2xl md:text-3xl font-semibold text-white">
          We do not map infrastructure.<br />
          <span className="text-[#ff4b2a]">We explain it — and measure its consequence.</span>
        </p>
      </motion.div>
    </div>
  </section>
);

// Infralytics-GIIS Product Section
const InfralyticsSection = () => (
  <section id="infralytics" className="section bg-[#111111]" data-testid="infralytics-section">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">Layer 01 · Product</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="infralytics-title">
              Infralytics-GIIS
            </h2>
            <p className="text-xl text-[#a1a1a1] mb-8">Global Infrastructure Intelligence System</p>

            <p className="text-lg text-[#a1a1a1] leading-relaxed mb-6">
              Infralytics-GIIS does not merely aggregate data — it <span className="text-white">interrogates</span> it. Revealing relationships, dependencies, and failure points across complex digital infrastructure through <span className="text-white">15 original composite indices</span> across three analytical layers.
            </p>
            <p className="text-lg text-[#a1a1a1] leading-relaxed mb-8">
              It integrates connectivity, network, compute, population, and regulatory data into a unified system view — applying reasoning to reveal dependencies, constraints, risks, and opportunities.
            </p>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="text-2xl font-bold text-[#ff4b2a]">15</p>
                <p className="text-xs text-[#a1a1a1] mt-1">Composite indices</p>
              </div>
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="text-2xl font-bold text-[#ff4b2a]">3</p>
                <p className="text-xs text-[#a1a1a1] mt-1">Analytical layers</p>
              </div>
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="text-2xl font-bold text-[#ff4b2a]">1</p>
                <p className="text-xs text-[#a1a1a1] mt-1">Unified system view</p>
              </div>
            </div>

            <a
              href="https://www.infralytics.co"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTA('See How It Works', 'explore_product', 'infralytics_section')}
              className="btn-primary inline-flex items-center"
              data-testid="infralytics-cta-btn"
            >
              See How It Works
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </div>

          <div className="relative">
            <div className="glass-panel rounded-2xl p-8">
              <img
                src="https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                alt="Server infrastructure"
                className="rounded-xl mb-6 opacity-80"
              />
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 rounded-lg bg-white/5">
                  <Layers className="h-6 w-6 text-[#ff4b2a] mx-auto mb-2" />
                  <p className="text-sm text-[#a1a1a1]">Multi-layer</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <Network className="h-6 w-6 text-[#ff4b2a] mx-auto mb-2" />
                  <p className="text-sm text-[#a1a1a1]">Connected</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <Globe className="h-6 w-6 text-[#ff4b2a] mx-auto mb-2" />
                  <p className="text-sm text-[#a1a1a1]">Global</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// Infralytics-GIIS Live Showcase — North East Digital Infrastructure Chart
const InfralyticsShowcaseSection = () => {
  const capabilities = [
    {
      icon: BarChart3,
      title: "Fibre build & network resilience indices",
      desc: "Track full-fibre and gigabit rollout progress alongside resilience scoring — composite indices that turn raw rollout data into a governable signal.",
    },
    {
      icon: Landmark,
      title: "Compare across 12 North East Local Authorities",
      desc: "Side-by-side benchmarking of every Local Authority in the North East of England — see who is leading, who is lagging, and where intervention will move the needle.",
    },
    {
      icon: Search,
      title: "Search any gigabit postcode",
      desc: "Drill from regional aggregate to single-postcode reality. Gigabit availability, fibre coverage, and resilience signals in one query.",
    },
    {
      icon: Sparkles,
      title: "Chat with a trained Infralytics AI",
      desc: "Ask plain-language questions and get explainable, source-cited analysis across the North East Digital Infrastructure Chart — no dashboard tour required.",
    },
  ];

  return (
    <section className="section bg-[#0a0a0a] relative overflow-hidden" data-testid="infralytics-showcase-section">
      <div className="absolute inset-0 hero-glow opacity-30" />
      <div className="max-w-7xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-3xl mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono text-xs uppercase tracking-widest text-[#ff4b2a]">Live · North East of England</span>
              <span className="w-2 h-2 rounded-full bg-[#ff4b2a] animate-pulse" />
            </div>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="showcase-title">
              The North East Digital Infrastructure Chart.
            </h2>
            <p className="text-lg md:text-xl text-[#a1a1a1] leading-relaxed">
              Infralytics-GIIS deployed across the <span className="text-white">12 Local Authorities of the North East of England</span> — measuring fibre build progress, resilience, and gigabit availability at every postcode, with a trained AI ready to interrogate the data on your behalf.
            </p>
          </div>

          {/* Video */}
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black shadow-2xl shadow-[#ff4b2a]/10 mb-12" data-testid="showcase-video-wrapper">
            <video
              src="https://customer-assets.emergentagent.com/job_ccd64a72-8bda-456b-af57-b3376f8be905/artifacts/7kojy3rg_Nexintell%20Infralytics%20video.mp4"
              controls
              playsInline
              preload="metadata"
              poster=""
              className="w-full h-auto block"
              data-testid="showcase-video"
            >
              Your browser does not support the video tag.
            </video>
            <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur border border-white/10 pointer-events-none">
              <PlayCircle className="h-4 w-4 text-[#ff4b2a]" />
              <span className="text-xs font-mono uppercase tracking-widest text-white">Product walkthrough</span>
            </div>
          </div>

          {/* Capability grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {capabilities.map((cap, idx) => (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="glass-panel rounded-xl p-6 border border-white/10 hover:border-[#ff4b2a]/30 transition-colors"
                data-testid={`showcase-capability-${idx}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-[#ff4b2a]/10 border border-[#ff4b2a]/30 flex items-center justify-center flex-shrink-0">
                    <cap.icon className="h-5 w-5 text-[#ff4b2a]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{cap.title}</h3>
                    <p className="text-[#a1a1a1] leading-relaxed">{cap.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href="https://www.infralytics.co"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTA('Open Infralytics Chart', 'explore_product', 'showcase')}
              className="btn-primary inline-flex items-center"
              data-testid="showcase-cta-btn"
            >
              Open the North East Chart
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
            <p className="text-sm text-[#a1a1a1]">
              Built on 15 composite indices across 3 analytical layers — explainable by design.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};



// Adoptic Video Carousel — one video at a time, prev/next + dots
const ADOPTIC_VIDEOS = [
  'https://customer-assets.emergentagent.com/job_ccd64a72-8bda-456b-af57-b3376f8be905/artifacts/8jqlvvlw_Nexintell%20Adoptic%20V1.MP4',
  'https://customer-assets.emergentagent.com/job_ccd64a72-8bda-456b-af57-b3376f8be905/artifacts/6glqj5c7_Adoptic%202.MP4',
  'https://customer-assets.emergentagent.com/job_ccd64a72-8bda-456b-af57-b3376f8be905/artifacts/l8b3o14u_Adoptic%203.MP4',
  'https://customer-assets.emergentagent.com/job_ccd64a72-8bda-456b-af57-b3376f8be905/artifacts/jcyz91is_Adoptic%204.MP4',
];

const AdopticVideoCarousel = () => {
  const [index, setIndex] = useState(0);
  const total = ADOPTIC_VIDEOS.length;
  const goPrev = () => setIndex((i) => (i - 1 + total) % total);
  const goNext = () => setIndex((i) => (i + 1) % total);

  return (
    <div className="glass-panel rounded-2xl p-4 sm:p-6 border border-white/10" data-testid="adoptic-video-carousel">
      <div className="relative rounded-xl overflow-hidden bg-black">
        <video
          key={index}
          src={ADOPTIC_VIDEOS[index]}
          controls
          playsInline
          preload="metadata"
          className="w-full h-auto block"
          data-testid={`adoptic-video-${index}`}
        >
          Your browser does not support the video tag.
        </video>

        {/* Prev arrow */}
        <button
          type="button"
          onClick={goPrev}
          aria-label="Previous video"
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-[#ff4b2a] backdrop-blur border border-white/20 flex items-center justify-center text-white transition-colors"
          data-testid="adoptic-video-prev"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        {/* Next arrow */}
        <button
          type="button"
          onClick={goNext}
          aria-label="Next video"
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-[#ff4b2a] backdrop-blur border border-white/20 flex items-center justify-center text-white transition-colors"
          data-testid="adoptic-video-next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Counter badge */}
        <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-xs font-mono text-white">
          {index + 1} / {total}
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex items-center justify-center gap-2 mt-4">
        {ADOPTIC_VIDEOS.map((url, i) => (
          <button
            key={url}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Go to video ${i + 1}`}
            className={`h-2 rounded-full transition-all ${
              i === index ? 'w-8 bg-[#ff4b2a]' : 'w-2 bg-white/20 hover:bg-white/40'
            }`}
            data-testid={`adoptic-video-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
};

// Adoptic Product Section
const AdopticSection = () => {
  const tellsDecisionMakers = [
    { icon: MapPin, text: "Postcode-level, real-time visibility for every business in a geography" },
    { icon: Activity, text: "Tracks connectivity investment and where it translates into economic activity" },
    { icon: Target, text: "Pinpoints intervention sites and quantifies the economic return on action" },
    { icon: Zap, text: "Continuously rerunnable — no six-month consultancy waits" }
  ];

  return (
    <section id="adoptic" className="section bg-[#0a0a0a] relative" data-testid="adoptic-section">
      <div className="absolute inset-0 hero-glow opacity-30" />
      <div className="max-w-7xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">Layer 02 · Product</p>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="adoptic-title">
                Adoptic
              </h2>
              <p className="text-xl text-[#a1a1a1] mb-8">
                <span className="text-white">Digital Adoption Optics.</span> The UK's first Business Digital Inclusion Intelligence platform.
              </p>

              <p className="text-lg text-[#a1a1a1] leading-relaxed mb-6">
                Adoptic makes digital adoption <span className="text-white">visible</span> — identifying which businesses are participating in the digital economy, which are not, and precisely where the divide exists.
              </p>
              <p className="text-lg text-[#a1a1a1] leading-relaxed mb-6">
                By combining connectivity infrastructure data, socioeconomic deprivation indices, and individual business digital footprint scoring at the postcode level, Adoptic gives local authorities, investors, and operators a clear way to <span className="text-white">see, measure, and bridge</span> the gap between businesses the digital economy is serving and those it is leaving behind.
              </p>

              <div className="flex items-center gap-3 mb-8">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff4b2a]/10 border border-[#ff4b2a]/30 text-[#ff4b2a] text-xs font-mono uppercase tracking-widest">
                  <span className="w-2 h-2 rounded-full bg-[#ff4b2a] animate-pulse" />
                  Live · Stockton-on-Tees
                </span>
              </div>

              <p className="text-base text-[#a1a1a1] leading-relaxed mb-8">
                Currently deployed as the <span className="text-white font-medium">Stockton Digital Register</span> — the first postcode-level business intelligence platform in the UK, linking broadband infrastructure, business digital footprint, and community deprivation.
              </p>

              <Button
                onClick={() => { trackCTA('Explore Adoptic', 'explore_product', 'adoptic_section'); window.open('https://stockton.infralytics.co', '_blank'); }}
                className="btn-primary inline-flex items-center"
                data-testid="adoptic-cta-btn"
              >
                Explore the Live Product
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </div>

            <div className="relative">
              <AdopticVideoCarousel />
            </div>
          </div>

          {/* What Adoptic tells decision-makers */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="glass-panel rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-semibold mb-6">What Adoptic tells decision-makers</h3>
              <p className="text-[#a1a1a1] mb-6 leading-relaxed">
                Not just what infrastructure exists, but <span className="text-white">whom it serves, whom it fails, exactly where to intervene, and what the economic return on that intervention will be.</span>
              </p>
              <ul className="space-y-4">
                {tellsDecisionMakers.map((item, idx) => (
                  <li key={item.text} className="flex items-start gap-3 text-[#a1a1a1]" data-testid={`adoptic-feature-${idx}`}>
                    <item.icon className="h-5 w-5 text-[#ff4b2a] flex-shrink-0 mt-0.5" />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              <div className="glass-panel rounded-xl p-6 border border-white/10" data-testid="adoptic-vs-ofcom">
                <p className="font-mono text-xs uppercase tracking-widest text-[#ff4b2a] mb-2">Unlike the Ofcom dashboard</p>
                <p className="text-[#a1a1a1] leading-relaxed">Tells you where the cable is — but cannot tell you whether a single business on that street is using it, benefiting from it, or being left behind by it.</p>
              </div>
              <div className="glass-panel rounded-xl p-6 border border-white/10" data-testid="adoptic-vs-esri">
                <p className="font-mono text-xs uppercase tracking-widest text-[#ff4b2a] mb-2">Unlike ESRI / IDOX</p>
                <p className="text-[#a1a1a1] leading-relaxed">Manages infrastructure beautifully, but cannot model human impact or economic consequences.</p>
              </div>
              <div className="glass-panel rounded-xl p-6 border border-white/10" data-testid="adoptic-vs-consultancy">
                <p className="font-mono text-xs uppercase tracking-widest text-[#ff4b2a] mb-2">Unlike consultancy reports</p>
                <p className="text-[#a1a1a1] leading-relaxed">No six months and £50,000 producing a report outdated before it is printed. Adoptic can be rerun continuously.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// "This is for you" / Adoptic audience prompts
const AdopticForYouSection = () => {
  const prompts = [
    { icon: Landmark, text: "If you work in a Local Authority team responsible for digital inclusion — and you do not have a postcode-level baseline to measure progress." },
    { icon: HeartHandshake, text: "If you are part of a Business Support Organisation trying to reach the businesses that need your help most but are hard to find." },
    { icon: Radio, text: "If you are an ISP or investor who built the network — but have no way to know if it is encouraging economic participation." },
    { icon: Briefcase, text: "If you have ever walked past a great local business and wondered why they are not online." }
  ];

  return (
    <section className="section bg-[#111111]" data-testid="adoptic-for-you-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">This is for you</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-12 max-w-3xl">
            If you can see the cable but not the consequence — Adoptic was built for you.
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {prompts.map((item, idx) => (
              <motion.div
                key={item.text}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="flex gap-4 p-6 rounded-xl bg-white/5 border border-white/10 hover:border-[#ff4b2a]/30 transition-colors"
                data-testid={`for-you-${idx}`}
              >
                <item.icon className="h-6 w-6 text-[#ff4b2a] flex-shrink-0 mt-1" />
                <p className="text-base md:text-lg text-[#d4d4d4] leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Founder Section
const FounderSection = () => (
  <section className="section bg-[#0a0a0a] relative" data-testid="founder-section">
    <div className="absolute inset-0 hero-glow opacity-20" />
    <div className="max-w-7xl mx-auto relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">Origin</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Built from a decade of building infrastructure — and watching the consequence go unmeasured.
            </h2>
            <p className="text-lg text-[#a1a1a1] leading-relaxed">
              Nexintell was founded by <span className="text-white font-medium">Ogaba Joseph Attah</span>, after a decade building digital infrastructure across Africa.
            </p>
          </div>
          <div className="lg:col-span-3 space-y-5 text-[#a1a1a1] leading-relaxed text-base md:text-lg">
            <p>
              He identified a critical gap: the inability to measure the human impact of multi-million dollar infrastructure investments. No framework existed to translate technical performance into measurable, accessible human outcomes.
            </p>
            <p>
              UK research combining Ofcom connectivity data, ONS deprivation indices, and Companies House records revealed it sharply — <span className="text-white">95% ultra-fast broadband coverage coexists with 55% business invisibility online</span>, especially in deprived areas. Infrastructure available, participation absent.
            </p>
            <p className="text-white font-medium">
              Infrastructure without adoption is incomplete. Nexintell measures both.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      title: "Inputs",
      items: ["Public fibre maps", "Ofcom connectivity data", "ISP claims & IXPs", "Submarine cables", "Companies House records", "ONS deprivation indices", "Population density", "Cloud regions & policy datasets"]
    },
    {
      title: "Reasoning",
      items: ["System dependencies", "Capacity constraints", "Resilience risks", "Business digital footprint scoring", "Exclusion diagnostics", "Scenario testing"]
    },
    {
      title: "Outputs",
      items: ["Planning recommendations", "Governance views", "Investment intelligence", "Resilience playbooks", "Postcode-level inclusion maps", "Quantified return on intervention"]
    }
  ];

  return (
    <section className="section bg-[#111111]" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">Process</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-12">
            How the platform works.
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                className="relative"
                data-testid={`step-${idx}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#ff4b2a] text-white font-bold">
                    {idx + 1}
                  </span>
                  <h3 className="text-2xl font-semibold">{step.title}</h3>
                </div>

                <ul className="space-y-3">
                  {step.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[#a1a1a1]">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#ff4b2a] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                {idx < 2 && (
                  <div className="hidden md:block absolute top-5 -right-4 text-[#262626]">
                    <ChevronRight className="h-8 w-8" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Who It's For Section
const AudienceSection = () => {
  const audiences = [
    { icon: Landmark, name: "Governments", desc: "National digital infrastructure planning" },
    { icon: Building2, name: "Local Authorities", desc: "Postcode-level digital inclusion" },
    { icon: HeartHandshake, name: "Business Support Orgs", desc: "Reach businesses that need help most" },
    { icon: CircuitBoard, name: "Operators & ISPs", desc: "Network optimization & adoption insight" },
    { icon: DollarSign, name: "Investors", desc: "Infrastructure & inclusion intelligence" },
    { icon: CloudLightning, name: "Hyperscalers", desc: "Global compute & network decisions" }
  ];

  return (
    <section id="audience" className="section bg-[#0a0a0a]" data-testid="audience-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">Target Audience</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-12">
            Built for infrastructure decision-makers.
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {audiences.map((item, idx) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                className="feature-card text-center py-8"
                data-testid={`audience-card-${idx}`}
              >
                <item.icon className="h-10 w-10 text-[#ff4b2a] mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{item.name}</h3>
                <p className="text-sm text-[#a1a1a1]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Now Section
const WhyNowSection = () => {
  const reasons = [
    { icon: DollarSign, title: "Cost Pressure", desc: "Infrastructure budgets under strain" },
    { icon: ShieldCheck, title: "Regulation", desc: "New compliance requirements" },
    { icon: CloudLightning, title: "Climate Risk", desc: "Resilience becomes critical" },
    { icon: Users, title: "Inequality", desc: "Digital divide deepening" },
    { icon: Network, title: "System Complexity", desc: "Dependencies multiplying" }
  ];

  return (
    <section className="section bg-[#111111]" data-testid="why-now-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">Timing</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-12">
            Why this matters now.
          </h2>

          <div className="flex flex-wrap gap-4 justify-center">
            {reasons.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-6 py-3"
                data-testid={`reason-${idx}`}
              >
                <item.icon className="h-5 w-5 text-[#ff4b2a]" />
                <span className="font-medium">{item.title}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = ({ onDemoClick, onContactClick }) => (
  <section className="section bg-[#0a0a0a] relative" data-testid="final-cta-section">
    <div className="absolute inset-0 hero-glow" />
    <div className="max-w-7xl mx-auto relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center max-w-3xl mx-auto"
      >
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-8" data-testid="final-cta-title">
          See both dimensions of your infrastructure.
        </h2>

        <p className="text-lg md:text-xl text-[#a1a1a1] leading-relaxed mb-10">
          If you are responsible for planning, governing, or investing in digital infrastructure — Nexintell shows you not only the system, but the consequence. The cable, and the business it serves.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => { trackCTA('Request a Demo Final', 'demo_request', 'final_cta'); onDemoClick(); }}
            className="btn-primary text-lg px-10 py-6"
            data-testid="final-demo-btn"
          >
            Request a Demo
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            onClick={() => { trackCTA('Contact Partnerships', 'contact_partnership', 'final_cta'); onContactClick(); }}
            className="btn-secondary text-lg px-8 py-6"
            data-testid="final-contact-btn"
          >
            Contact Partnerships
          </Button>
        </div>
      </motion.div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-12 px-6 md:px-12 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-6">
        <div className="flex items-center gap-4">
          <Logo className="h-8 md:h-10" />
          <span className="text-sm text-[#a1a1a1]">Infrastructure × Consequence Intelligence</span>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <a
            href="/commitments"
            className="text-[#a1a1a1] hover:text-white transition-colors"
            data-testid="footer-commitments-link"
          >
            Our Commitments
          </a>
          <a
            href="/privacy"
            className="text-[#a1a1a1] hover:text-white transition-colors"
            data-testid="footer-privacy-link"
          >
            Privacy Policy
          </a>
          <div className="flex items-center gap-3 pl-3 border-l border-white/10">
            <a
              href="https://www.linkedin.com/company/nexintell-limited/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Nexintell on LinkedIn"
              className="text-[#a1a1a1] hover:text-[#ff4b2a] transition-colors"
              data-testid="footer-linkedin-link"
            >
              <Linkedin className="h-5 w-5" />
            </a>
            <a
              href="https://www.instagram.com/infralytics?igsh=MTZyOWJnYWoyOHJvdQ=="
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Infralytics on Instagram"
              className="text-[#a1a1a1] hover:text-[#ff4b2a] transition-colors"
              data-testid="footer-instagram-link"
            >
              <Instagram className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-6 border-t border-white/5">
        <div className="flex items-center gap-6 text-sm text-[#a1a1a1]">
          <a href="mailto:contact@nexintell.co" className="hover:text-[#ff4b2a] transition-colors">
            contact@nexintell.co
          </a>
          <a href="tel:+447867038150" className="hover:text-[#ff4b2a] transition-colors">
            +44 7867 038150
          </a>
        </div>
        <div className="text-sm text-[#a1a1a1]">
          © {new Date().getFullYear()} Nexintell Ltd. All rights reserved. Founded in the UK.
        </div>
      </div>
    </div>
  </footer>
);

// Demo Request Dialog
const DemoRequestDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/demo-request`, formData);
      toast.success('Demo request submitted! We\'ll be in touch soon.');
      onOpenChange(false);
      setFormData({ name: '', email: '', company: '', role: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-[#262626] text-white sm:max-w-md" data-testid="demo-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Request a Demo</DialogTitle>
          <DialogDescription className="text-[#a1a1a1]">
            Fill out the form and our team will reach out to schedule a personalized demo.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              className="input-field"
              data-testid="demo-name-input"
            />
          </div>
          <div>
            <Input
              type="email"
              placeholder="Work Email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="input-field"
              data-testid="demo-email-input"
            />
          </div>
          <div>
            <Input
              placeholder="Company"
              value={formData.company}
              onChange={(e) => setFormData({...formData, company: e.target.value})}
              required
              className="input-field"
              data-testid="demo-company-input"
            />
          </div>
          <div>
            <Input
              placeholder="Your Role"
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              required
              className="input-field"
              data-testid="demo-role-input"
            />
          </div>
          <div>
            <Textarea
              placeholder="Tell us about your infrastructure challenges (optional)"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              className="input-field min-h-[100px]"
              data-testid="demo-message-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
            data-testid="demo-submit-btn"
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Landing Page
export default function LandingPage() {
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);

  const scrollToApproach = () => {
    document.getElementById('approach')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="landing-page">
      <div className="noise-overlay" />

      <Navigation onDemoClick={() => setDemoDialogOpen(true)} />

      <HeroSection
        onDemoClick={() => setDemoDialogOpen(true)}
        onExploreClick={scrollToApproach}
      />
      <ProblemSection />
      <WhyToolsFailSection />
      <ApproachSection />
      <InfralyticsSection />
      <InfralyticsShowcaseSection />
      <AdopticSection />
      <AdopticForYouSection />
      <FounderSection />
      <HowItWorksSection />
      <AudienceSection />
      <WhyNowSection />
      <FinalCTASection
        onDemoClick={() => setDemoDialogOpen(true)}
        onContactClick={() => setDemoDialogOpen(true)}
      />
      <Footer />

      <DemoRequestDialog
        open={demoDialogOpen}
        onOpenChange={setDemoDialogOpen}
      />
    </div>
  );
}
